/**
 * useCosts Hook
 *
 * React Query hooks for investment cost operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import { costsService, CreateCostDto, UpdateCostDto, CostSummary } from '../services/costs.service';
import { unwrapResponse } from '../types';

import { investmentKeys } from './useInvestments';

import type { InvestmentCost, InvestmentCostWithCategory } from '@medsuite/models';

export const costKeys = {
  all: ['costs'] as const,
  lists: () => [...costKeys.all, 'list'] as const,
  list: (investmentId: number) => [...costKeys.lists(), investmentId] as const,
  summary: (investmentId: number) => [...costKeys.all, 'summary', investmentId] as const,
};

/**
 * Fetch all costs for an investment
 */
export function useCosts(
  investmentId: number,
  options?: Omit<UseQueryOptions<InvestmentCostWithCategory[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentCostWithCategory[], Error>({
    queryKey: costKeys.list(investmentId),
    queryFn: async () => {
      const response = await costsService.getAll(investmentId);
      // Extract data from success wrapper: {success: true, data: {investment_id, costs[], total, summary}, meta: {}}
      const responseData = unwrapResponse<{ costs?: InvestmentCostWithCategory[] }>(response.data);
      // The API returns {investment_id, costs, total, summary}, extract the costs array
      const costs = responseData.costs || [];

      // Convert string values to numbers (database returns DECIMAL as strings)
      return costs.map((cost: InvestmentCostWithCategory) => ({
        ...cost,
        capex_cost: parseFloat(String(cost.capex_cost)) || 0,
        opex_cost: parseFloat(String(cost.opex_cost)) || 0,
      }));
    },
    ...options,
  });
}

/**
 * Fetch cost summary with totals
 */
export function useCostSummary(
  investmentId: number,
  options?: Omit<UseQueryOptions<CostSummary, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<CostSummary, Error>({
    queryKey: costKeys.summary(investmentId),
    queryFn: async () => {
      const response = await costsService.getSummary(investmentId);
      // Extract data from success wrapper: {success: true, data: {summary}, meta: {}}
      const responseData = unwrapResponse<{ summary?: Record<string, unknown> }>(response.data);
      // The API returns {summary}, extract it
      const summary = (responseData.summary || responseData) as Record<string, unknown>;

      // Map API field names to expected names and parse string values to numbers
      return {
        ...summary,
        total_capex: parseFloat(String(summary.total_capex)) || 0,
        total_opex: parseFloat(String(summary.total_opex)) || 0,
        grand_total: parseFloat(String(summary.total)) || 0,
        by_category:
          (summary.by_category as Record<string, unknown>[])?.map((cat: Record<string, unknown>) => ({
            ...cat,
            total: parseFloat(String(cat.total)) || 0,
          })) || [],
      } as CostSummary;
    },
    ...options,
  });
}

/**
 * Create new cost
 */
export function useCreateCost(
  investmentId: number,
  options?: UseMutationOptions<InvestmentCost, Error, Omit<CreateCostDto, 'investment_id'>>
) {
  const queryClient = useQueryClient();

  return useMutation<InvestmentCost, Error, Omit<CreateCostDto, 'investment_id'>>({
    mutationFn: async (data) => {
      const response = await costsService.create(investmentId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: costKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: costKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
    },
    ...options,
  });
}

/**
 * Update cost
 */
export function useUpdateCost(
  investmentId: number,
  options?: UseMutationOptions<InvestmentCost, Error, { costId: number; data: UpdateCostDto }>
) {
  const queryClient = useQueryClient();

  return useMutation<InvestmentCost, Error, { costId: number; data: UpdateCostDto }>({
    mutationFn: async ({ costId, data }) => {
      const response = await costsService.update(investmentId, costId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: costKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: costKeys.summary(investmentId) });
    },
    ...options,
  });
}

/**
 * Delete cost
 */
export function useDeleteCost(
  investmentId: number,
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (costId) => {
      await costsService.delete(investmentId, costId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: costKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: costKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
    },
    ...options,
  });
}
